import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { PLANS, hasFreePass } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, ShoppingBag, Tag, Sparkles, Gift } from "lucide-react";
import { toast } from "sonner";

function PlanCard({ plan, onChoose, loading, featured }) {
  return (
    <div data-testid={`plan-card-${plan.key}`}
      className={`relative rounded-3xl p-6 border-2 bg-white flex flex-col ${
        featured ? "border-ys-sky shadow-xl shadow-sky-100" : "border-slate-200"
      }`}>
      {featured && (
        <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-ys-sky text-white text-xs font-bold font-mono uppercase">Popular</span>
      )}
      <h3 className="font-display text-xl font-bold">{plan.name}</h3>
      <div className="mt-3 flex items-end gap-1">
        <span className="text-4xl font-extrabold">${plan.price}</span>
        <span className="text-slate-500 mb-1">/{plan.unit}</span>
      </div>
      {plan.sub && <p className="text-xs text-ys-sky font-semibold mt-1">Auto-renews on the 1st · cancel anytime</p>}
      <ul className="space-y-2.5 mt-5 flex-1">
        {plan.perks.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm text-slate-600">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {p}
          </li>
        ))}
      </ul>
      <Button onClick={() => onChoose(plan)} disabled={loading}
        data-testid={`choose-plan-${plan.key}`}
        className={`mt-6 rounded-full h-12 ${featured ? "bg-ys-sky hover:bg-sky-700" : "bg-slate-900 hover:bg-slate-800"}`}>
        {loading ? "Redirecting…" : plan.sub ? "Subscribe" : "Get Pass"}
      </Button>
    </div>
  );
}

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState("");
  const [pricing, setPricing] = useState(null);
  const [savedCard, setSavedCard] = useState(null);

  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) api.get("/payments/saved-card").then(({ data }) => setSavedCard(data.saved_card)).catch(() => {}); }, [user]);

  const withLive = (list) => list.map((p) => ({ ...p, price: pricing?.[p.key] ?? p.price }));

  const choose = async (plan) => {
    if (!user) { navigate("/register"); return; }
    setLoading(plan.key);
    try {
      if (savedCard) {
        await api.post("/payments/quick-charge", { lookup_key: plan.key });
        await refreshUser();
        toast.success("Paid instantly with your saved card — you're all set!");
        navigate(plan.key.startsWith("seller") ? "/profile?tab=listings" : "/browse");
        return;
      }
      const { data } = await api.post("/payments/checkout", { lookup_key: plan.key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setLoading("");
    }
  };

  if (hasFreePass(user)) {
    const exp = user.free_pass.expires;
    return (
      <div className="min-h-screen bg-ys-canvas">
        <Header />
        <div className="max-w-xl mx-auto px-4 py-16">
          <div className="rounded-3xl border-2 border-ys-sky bg-white p-8 text-center shadow-xl shadow-sky-100" data-testid="free-pass-plan-card">
            <Gift className="w-12 h-12 text-ys-gold mx-auto" />
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-4">You're on Pro Seller — free</h1>
            <p className="text-slate-600 mt-2">An admin gifted you an all-inclusive pass: unlimited listings, business listings and full browsing. No plan to pick and no card needed.</p>
            <p className="text-sm font-semibold text-ys-sky mt-3" data-testid="free-pass-plan-expiry">{exp ? `Free pass until ${new Date(exp).toLocaleDateString()}` : "Free pass — never expires"}</p>
            <div className="flex gap-3 justify-center mt-6 flex-wrap">
              <Button onClick={() => navigate("/create")} data-testid="free-pass-post-btn" className="rounded-full h-11 bg-ys-sky hover:bg-sky-700">Post a listing</Button>
              <Button onClick={() => navigate("/browse")} variant="outline" data-testid="free-pass-browse-btn" className="rounded-full h-11">Browse sales</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ys-canvas">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Simple, fair pricing</h1>
          <p className="text-slate-600 mt-3">Pay for a week or subscribe by the month. Mid-month subscriptions are pro-rated, and everything renews on the 1st.</p>
          {savedCard && <p className="mt-3 inline-flex items-center gap-2 text-sm bg-sky-50 text-ys-sky rounded-full px-4 py-1.5 font-semibold" data-testid="pricing-saved-card-note">💳 Card on file •••• {savedCard.last4} — purchases are one tap.</p>}
        </div>

        <Tabs defaultValue="buyer" className="mt-10">
          <TabsList className="mx-auto flex w-fit rounded-full bg-slate-100 p-1" data-testid="pricing-tabs">
            <TabsTrigger value="buyer" className="rounded-full gap-2 data-[state=active]:bg-white px-5"><ShoppingBag className="w-4 h-4" /> Shoppers</TabsTrigger>
            <TabsTrigger value="seller" className="rounded-full gap-2 data-[state=active]:bg-white px-5"><Tag className="w-4 h-4" /> Sellers</TabsTrigger>
          </TabsList>

          <TabsContent value="buyer" className="mt-8">
            <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {withLive(PLANS.buyer).map((p, i) => <PlanCard key={p.key} plan={p} onChoose={choose} loading={loading === p.key} featured={i === 1} />)}
            </div>
          </TabsContent>
          <TabsContent value="seller" className="mt-8">
            <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {withLive(PLANS.seller).map((p, i) => <PlanCard key={p.key} plan={p} onChoose={choose} loading={loading === p.key} featured={i === 1} />)}
            </div>
            <p className="text-center text-sm text-slate-500 mt-6 max-w-xl mx-auto">
              Pro Seller is all-inclusive: unlimited selling <strong>and</strong> full shopper browsing for the month. You can add "Highlight my goodies" to any listing from your account after you sign up.
            </p>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
